import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import ReactTableWrapper from "../../../Components/common/ReactTableWrapper/ReactTableWrapper";
import CustomSelect from "../../../Components/common/CustomSelect/CustomSelect";
import Warning from "../../../Components/common/Warning/Warning";
import AgentURLPopup from "./components/PopupForm/AgentURLPopup";
import AgentOsPopup from "./components/PopupForm/AgentOsPopup";
import Backdrop from "../../../Components/UI/Backdrop/Backdrop";
import DownloadMessage from "../../../Components/common/DownloadMessage/DownloadMessage";

import useDownloadCFSetup from "../../../utils/hook/useDownloadCFSetup";
import useDownloadCFAgent from "../../../utils/hook/useDownloadCFAgent";
import { agentInstallationClientHeader } from "../../../utils/constants/tableHeaders";
import {
  RequestAgentInstallationList,
  RequestAllAgentGroups,
  RequestBulkAnalyzeAgents,
  RequestBulkYaraScanAgents,
  RequestBulkDeleteAgents,
} from "../../../store/actions/integrationsActions";
import { addNotification } from "../../../store/actions/notifierActions";
import { checkLicenseForAgent } from "../../../utils/function";

import "./styles.scss";

const filteringHeader = [
  { id: "none", value: "Sort Options..." },
  { id: "hostname", value: "Host Name" },
  { id: "username", value: "User Name" },
  { id: "macAddress", value: "Mac Address" },
  { id: "localIp", value: "Local IP" },
];

const onlineStatusOptions = [
  { id: "online", value: "Show Online" },
  { id: "offline", value: "Show Offline" },
  { id: "all", value: "Show All" },
];

const ALL_GROUPS_OPTION = { id: "all", value: "All Groups" };
const NO_GROUP_OPTION = { id: "none", value: "No Group" };

const AgentInstallation = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [selectedTimeFilter, setSelectedTimeFilter] = useState(null);
  const [show, setShow] = useState(false);
  const [showDefaultInstallerOs, setShowDefaultInstallerOs] = useState(false);
  const [showCollectorOs, setShowCollectorOs] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState(filteringHeader[0]);
  const [onlineFilter, setOnlineFilter] = useState(onlineStatusOptions[2]);
  const [selectedGroup, setSelectedGroup] = useState(ALL_GROUPS_OPTION);

  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkAction, setBulkAction] = useState(false);
  const [bulkMessage, setBulkMessage] = useState("");

  const licenceCheck = useSelector((state) => state?.users.licenceCheck);
  const isLicenseOkay = checkLicenseForAgent(licenceCheck.licenceStatus, licenceCheck.integrations);

  const agentInstallationList = useSelector((state) => state.integrations.agentInstallationList);
  const agentInstallationListLoading = useSelector((state) => state.integrations.loading);
  const allAgentGroups = useSelector((state) => state.integrations.allAgentGroups);

  const protocol = window.location.protocol;
  const hostname = window.location.hostname;

  const { downloadOptions, fileName, defaultInstallerHandler } = useDownloadCFSetup(hostname);
  const {
    downloadOptions: cfagentDownloadOptions,
    fileName: cfagentFileName,
    standaloneAgentDownloadHandler,
  } = useDownloadCFAgent();

  const refreshOnClickHandler = useCallback(() => {
    if (selectedTimeFilter) {
      dispatch(RequestAgentInstallationList({ lastUpdatedTimeFilter: selectedTimeFilter }));
    }
  }, [selectedTimeFilter, dispatch]);

  useEffect(() => {
    if (selectedTimeFilter) {
      dispatch(RequestAgentInstallationList({ lastUpdatedTimeFilter: selectedTimeFilter }));
    }
  }, [selectedTimeFilter, dispatch]);

  useEffect(() => {
    dispatch(RequestAllAgentGroups());
  }, [dispatch]);

  const groupOptions = useMemo(() => {
    const groups = allAgentGroups || [];

    // The same group name can exist on more than one threat hunter host, so only
    // those get the host appended.
    const nameCounts = groups.reduce((counts, group) => {
      counts[group.name] = (counts[group.name] || 0) + 1;
      return counts;
    }, {});

    return [
      ALL_GROUPS_OPTION,
      NO_GROUP_OPTION,
      ...groups.map((group) => ({
        id: group.compositeId,
        value: nameCounts[group.name] > 1 ? `${group.name} (${group.host})` : group.name,
      })),
    ];
  }, [allAgentGroups]);

  const filteredAgentData = useMemo(() => {
    return [...agentInstallationList]
      .sort((a, b) => {
        if (sortField.id !== "none" && a[sortField.id] && b[sortField.id]) {
          return a[sortField.id].localeCompare(b[sortField.id]);
        }
        return 0;
      })
      .filter((agent) => {
        const searchFields = [agent.hostname, agent.username, agent.macAddress, agent.localIp];
        const matchesSearch = searchFields.some((field) =>
          field?.toLowerCase().includes(searchTerm.toLowerCase()),
        );

        const matchesOnline =
          onlineFilter.id === "all"
            ? true
            : onlineFilter.id === "online"
              ? agent.onlineStatus === true
              : agent.onlineStatus === false;

        const groups = agent.groups || [];
        const matchesGroup =
          selectedGroup.id === "all"
            ? true
            : selectedGroup.id === "none"
              ? groups.length === 0
              : groups.includes(selectedGroup.id);

        return matchesSearch && matchesOnline && matchesGroup;
      });
  }, [agentInstallationList, searchTerm, sortField, onlineFilter, selectedGroup]);

  const generateInstallerHandler = () => {
    if (isLicenseOkay) {
      setShow(true);
    } else {
      dispatch(
        addNotification({
          message: "Your license key has expired. Please contact CyberFortress Technology Team.",
          options: { variant: "error" },
        }),
      );
    }
  };

  const licenseErrorNotification = () =>
    dispatch(
      addNotification({
        message: "Your license key has expired. Please contact CyberFortress Technology Team.",
        options: { variant: "error" },
      }),
    );

  const defaultInstallerOnClick = () => {
    if (isLicenseOkay) {
      setShowDefaultInstallerOs(true);
    } else {
      licenseErrorNotification();
    }
  };

  const standAloneOnClick = () => {
    if (isLicenseOkay) {
      setShowCollectorOs(true);
    } else {
      licenseErrorNotification();
    }
  };

  const agentInstallationRowOnClickHandler = (row) => {
    const id = row?.original?.id;
    const decision = row?.original?.decision;

    // const date = row?.original?.date;

    // const encodedDate = encodeURIComponent(date);

    if (decision != null && decision !== 100 && id)
      history.push(`/analysis-reports?data=${id}&type=threat-hunter`);
  };

  const selectedCount = selectedRows.length;
  const agentLabel = `${selectedCount} agent${selectedCount < 2 ? "" : "s"}`;

  const startBulkAction = (action, message) => {
    if (!isLicenseOkay) {
      dispatch(
        addNotification({
          message: "Your license key has expired. Please contact CyberFortress Technology Team.",
          options: { variant: "error" },
        }),
      );
      return;
    }

    setBulkAction(action);
    setBulkMessage(message);
  };

  const bulkConfirmationHandler = () => {
    const agentIds = selectedRows.map((agent) => agent.agent_id).filter(Boolean);
    setBulkAction(false);

    if (bulkAction === "bulk-scan") {
      dispatch(RequestBulkAnalyzeAgents({ agentIds }));
    } else if (bulkAction === "bulk-yara-scan") {
      dispatch(RequestBulkYaraScanAgents({ agentIds }));
    } else if (bulkAction === "bulk-delete") {
      dispatch(RequestBulkDeleteAgents({ agentIds }));
    }
  };

  return (
    <div className="agent-installation">
      <div className="agent-installation__header">
        <h1>Agent Installation</h1>
        <p>You can find installed agents via generated links in this page:</p>
        <div className="agent-installation__header__installer-buttons">
          <button className="primary-button" onClick={generateInstallerHandler}>
            Generate Installer
          </button>
          <button className="primary-button" onClick={defaultInstallerOnClick}>
            Default Installer
          </button>
          <button className="primary-button" onClick={standAloneOnClick}>
            Standalone Collector
          </button>
        </div>
      </div>

       <div className="search-sort-wrapper">
          <input
            type="text"
            placeholder="Host Search..."
            value={searchTerm}
            style={{ marginRight: "2em", width: "220px" }}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {/* Sort by field */}
          <div className="sort-dropdown">
            <div className="sort-dropdown__item">
              <CustomSelect
                width="152px"
                height="32px"
                data={filteringHeader}
                select={sortField}
                setSelected={setSortField}
              />
            </div>
          </div>

          <div className="agent-installation__toolbar-separator" />

          {/* Agent group */}
          <div className="sort-dropdown">
            <div className="sort-dropdown__item">
              <CustomSelect
                width="200px"
                height="32px"
                data={groupOptions}
                select={selectedGroup}
                setSelected={setSelectedGroup}
              />
            </div>
          </div>

          <div className="agent-installation__toolbar-separator" />

          <div className="sort-dropdown">
            <div className="sort-dropdown__item">
              <CustomSelect
                width="148px"
                height="32px"
                data={onlineStatusOptions}
                select={onlineFilter}
                setSelected={setOnlineFilter}
              />
            </div>
          </div>
        </div>

      {selectedCount > 0 && (
        <div className="agent-installation__bulk-bar border">
          <span className="agent-installation__bulk-bar__count">{agentLabel} selected</span>
          <button
            className="primary-button"
            onClick={() =>
              startBulkAction(
                "bulk-scan",
                `Are you sure to request analysis for the selected ${agentLabel}?`,
              )
            }>
            Request Scan
          </button>
          <button
            className="primary-button"
            onClick={() =>
              startBulkAction(
                "bulk-yara-scan",
                `Are you sure to request a custom YARA rule scan for the selected ${agentLabel}?`,
              )
            }>
            Request Yara Scan
          </button>
          <button
            className="primary-button agent-installation__bulk-bar__delete"
            onClick={() =>
              startBulkAction(
                "bulk-delete",
                `Are you sure to delete records for the selected ${agentLabel}? If you click yes, all the data will be deleted including scan reports.`,
              )
            }>
            Delete Record
          </button>
        </div>
      )}

      <div className="agent-installation__table border">
        <ReactTableWrapper
          data={filteredAgentData}
          isDataLoading={agentInstallationListLoading}
          tableName={"agentInstallation"}
          columns={agentInstallationClientHeader}
          paginationButtons
          customPageSize={10}
          refreshConfig={{ show: true, onClickHandler: refreshOnClickHandler }}
          filterByTime={useMemo(
            () => ({
              show: true,
              label: "Filter by last seen: ",
              selectedTimeFilter: selectedTimeFilter,
              setSelectedTimeFilter: setSelectedTimeFilter,
            }),
            [selectedTimeFilter, setSelectedTimeFilter],
          )}
          rowOnClickHandler={agentInstallationRowOnClickHandler}
          onSelectedRowsChange={setSelectedRows}
          // styles={{ table: { minHeight: "585px" } }}
        />
      </div>
      {show && <AgentURLPopup setShow={setShow} protocol={protocol} hostname={hostname} />}
      {showDefaultInstallerOs && (
        <AgentOsPopup
          title="Default Installer"
          description="Select the operating system of the machine the agent will be installed on."
          confirmText="Download Installer"
          setShow={setShowDefaultInstallerOs}
          onConfirm={defaultInstallerHandler}
        />
      )}
      {showCollectorOs && (
        <AgentOsPopup
          title="Standalone Collector"
          description="Select the operating system the standalone collector will run on."
          confirmText="Download Collector"
          setShow={setShowCollectorOs}
          onConfirm={standaloneAgentDownloadHandler}
        />
      )}
      <Warning
        onClickOk={bulkConfirmationHandler}
        show={bulkAction}
        setShow={setBulkAction}
        text={bulkMessage}
        article="Warning"
        cancelDisable={false}
      />
      <Backdrop
        show={downloadOptions.downloadStarted}
        message={
          <DownloadMessage
            fileName={fileName}
            loaded={downloadOptions.loaded}
            total={downloadOptions.total}
            progress={downloadOptions.progress}
          />
        }
      />
      <Backdrop
        show={cfagentDownloadOptions.downloadStarted}
        message={
          <DownloadMessage
            fileName={cfagentFileName}
            loaded={cfagentDownloadOptions.loaded}
            total={cfagentDownloadOptions.total}
            progress={cfagentDownloadOptions.progress}
          />
        }
      />
    </div>
  );
};

export default AgentInstallation;
